/**
 * CommandPalette
 *
 * Superhuman-style Cmd+K command palette with fuzzy search.
 */

import { useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import type { Command } from '../hooks/useCommandPalette';

interface CommandPaletteProps {
  open: boolean;
  query: string;
  onQueryChange: (query: string) => void;
  commands: Command[];
  onExecute: (commandId: string) => void;
  onClose: () => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  search: 'Search',
  compose: 'Compose',
  navigate: 'Navigate',
  action: 'Actions',
  ai: 'AI',
};

export function CommandPalette({
  open,
  query,
  onQueryChange,
  commands,
  onExecute,
  onClose,
}: CommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  // Group commands by category
  const grouped = commands.reduce<Record<string, Command[]>>((acc, cmd) => {
    if (!acc[cmd.category]) acc[cmd.category] = [];
    acc[cmd.category].push(cmd);
    return acc;
  }, {});

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Palette */}
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-lg z-50">
        <div className="bg-popover border border-border rounded-lg shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
            <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Type a command..."
              className="flex-1 bg-transparent text-sm outline-none"
              onKeyDown={(e) => {
                if (e.key === 'Escape') onClose();
                if (e.key === 'Enter' && commands.length > 0) {
                  onExecute(commands[0].id);
                }
              }}
            />
            <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">esc</kbd>
          </div>

          {/* Commands */}
          <div className="max-h-80 overflow-y-auto py-1">
            {commands.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No matching commands
              </div>
            ) : (
              Object.entries(grouped).map(([category, cmds]) => (
                <div key={category}>
                  <div className="px-4 py-1 text-xs font-medium text-muted-foreground">
                    {CATEGORY_LABELS[category] || category}
                  </div>
                  {cmds.map((cmd) => (
                    <button
                      key={cmd.id}
                      className="flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-muted transition-colors"
                      onClick={() => onExecute(cmd.id)}
                    >
                      <span>{cmd.label}</span>
                      {cmd.shortcut && (
                        <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {cmd.shortcut}
                        </kbd>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
